import React, { useEffect, useState, useRef } from 'react';
import { doc, getDoc, getDocs, updateDoc, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../hooks/useFirebase';
import { APP_ID } from '../constants/config';
import { Heart, ArrowLeft, Loader2, Share, Download, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import MetaTags from '../components/MetaTags';
import ThemeToggle from '../components/ThemeToggle';
import Toast from './Toast';

// Robust date parser to handle standard YYYY-MM-DD, ISO, and Notion CSV formats
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T12:00:00');
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

const getSortTime = (p) => {
  if (p.date) {
    const d = safeParseDate(p.date);
    if (d) return d.getTime();
  }
  if (p.year) {
    const d = new Date(p.year.toString());
    if (!isNaN(d.getTime())) return d.getTime();
  }
  return 0;
};

const SinglePoem = ({ poemId, handleNavigate, isDarkMode, toggleDarkMode, theme }) => {
  const [poem, setPoem] = useState(null);
  const [allPoems, setAllPoems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Swipe & Toast States
  const [toast, setToast] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const minSwipeDistance = 50;
  
  const cardRef = useRef(null);

  // Helper function to dynamically adjust font size based on poem length
  const getPoemFontSizeClass = (textLength = 0, forExport = false) => {
    if (textLength > 400) {
      return forExport ? 'text-xs' : 'text-xs md:text-sm';
    }
    if (textLength > 200) {
      return forExport ? 'text-sm' : 'text-sm md:text-base';
    }
    return forExport ? 'text-base' : 'text-base md:text-lg';
  };

  const pillStyle = { borderRadius: '9999px' };
  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    const fetchPoemAndLogView = async () => {
      setIsLoading(true);
      try {
        // 1. Fetch Poem
        const poemRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'poems', poemId);
        const snapshot = await getDoc(poemRef);
        
        if (snapshot.exists()) {
          setPoem({ id: snapshot.id, ...snapshot.data() });
          
          // 2. Check if user already liked it from localStorage
          const likedPoems = JSON.parse(localStorage.getItem('likedPoems') || '[]');
          if (likedPoems.includes(poemId)) {
            setIsLiked(true);
          }

          // 3. Log Analytics silently into your existing page_views
          const queryParams = new URLSearchParams(window.location.search);
          const source = queryParams.get('source') || 'direct';
          
          addDoc(collection(db, 'page_views'), {
            path: `/poem/${poemId}`,
            source: source,
            timestamp: serverTimestamp(),
            device: /Mobile|Android|iP(hone|od|ad)/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop'
          }).catch(err => console.error("Silent analytics failed", err));
        }
      } catch (error) {
        console.error("Error fetching poem:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (poemId) {
      fetchPoemAndLogView();
    }
  }, [poemId]);

  // Fetch all poems to enable next/previous navigation
  useEffect(() => {
    const fetchAllPoems = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'poems'));
        const poemsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        poemsData.sort((a, b) => getSortTime(b) - getSortTime(a));
        setAllPoems(poemsData);
      } catch (error) {
        console.error("Failed to fetch all poems for navigation:", error);
      }
    };
    fetchAllPoems();
  }, []);
  
  // Preload html-to-image and background images in the background
  useEffect(() => {
    import('html-to-image').catch(() => {});
    
    // Silently prefetch background image to ensure instant, high-quality rendering
    const img = new Image();
    img.src = '/Whatsapp%20Background%20Quote.png';
  }, []);

  const handleLike = async () => {
    const wasLiked = isLiked;
    
    setIsLiked(!wasLiked);
    setPoem(prev => ({ ...prev, likes: Math.max(0, (prev.likes || 0) + (wasLiked ? -1 : 1)) }));

    try {
      const likedPoems = JSON.parse(localStorage.getItem('likedPoems') || '[]');
      const newLikes = wasLiked ? likedPoems.filter(id => id !== poemId) : [...likedPoems, poemId];
      localStorage.setItem('likedPoems', JSON.stringify(newLikes));

      await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'poems', poemId), {
        likes: increment(wasLiked ? -1 : 1)
      });
    } catch (error) {
      console.error("Failed to like:", error);
      setIsLiked(wasLiked);
      setPoem(prev => ({ ...prev, likes: Math.max(0, (prev.likes || 0) + (wasLiked ? 1 : -1)) }));
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/?poem=${poemId}`;
    const shareText = `*${poem?.title || 'Sajak'}*\n\n"${poem?.text}"\n— ${poem?.author || 'Ben Hasan'}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: poem?.title || 'Sajak | idiawisdom.',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.error("Error sharing:", error);
      }
    } else {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      setIsDownloading(true);
      await document.fonts.ready;
      await new Promise(resolve => setTimeout(resolve, 150));

      const { toBlob } = await import('html-to-image');

      const blob = await toBlob(cardRef.current, {
        // Render from a fixed 360x640 CSS canvas at 3x for an exact
        // 1080x1920 WhatsApp Status image on every device.
        width: 360,
        height: 640,
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: isDarkMode ? '#1c1917' : '#fafaf9',
        style: {
          transform: 'none',
          width: '360px',
          height: '640px',
        }
      });

      if (!blob) throw new Error("Image generation failed");
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = (poem.title || 'verses').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      link.download = `idiawisdom-poem-${safeTitle}-${poemId}.png`;
      
      if (typeof link.download !== 'undefined') {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(url, '_blank');
      }
      
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      showToast("Poem card saved to device!");
    } catch (error) {
      console.error("Failed to download image:", error);
      showToast("Failed to save image. Please try again.", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const currentIndex = allPoems.findIndex(p => p.id === poemId);
  const prevPoemId = currentIndex > 0 ? allPoems[currentIndex - 1].id : null;
  const nextPoemId = currentIndex !== -1 && currentIndex < allPoems.length - 1 ? allPoems[currentIndex + 1].id : null;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && prevPoemId) {
        setSwipeDirection('right');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, prevPoemId); }, 400);
      } else if (e.key === 'ArrowRight' && nextPoemId) {
        setSwipeDirection('left');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, nextPoemId); }, 400);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevPoemId, nextPoemId, handleNavigate]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > minSwipeDistance;
    const isDownSwipe = distance < -minSwipeDistance;

    if (isUpSwipe && nextPoemId) {
      setSwipeDirection('up');
      setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, nextPoemId); }, 400);
    } else if (isDownSwipe && prevPoemId) {
      setSwipeDirection('down');
      setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, prevPoemId); }, 400);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className={`animate-spin ${theme?.textSubtle || 'text-stone-400'}`} size={40} />
      </div>
    );
  }

  if (!poem) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-6 text-center px-6 animate-in fade-in duration-500">
        <h1 className={`text-2xl font-serif ${theme?.text}`}>Poem not found.</h1>
        <button onClick={() => handleNavigate('poems')} style={pillStyle} className="px-6 py-3 border border-stone-200 dark:border-stone-800 text-xs font-bold uppercase tracking-widest text-stone-500 hover:text-stone-900 dark:hover:text-white transition-colors">
          Return to Archive
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative px-6 animate-in fade-in duration-1000">
      <MetaTags 
        title={poem.title || 'Sajak'} 
        description={poem.text} 
        url={`https://idiawisdom.com/?poem=${poemId}`} 
      />

      {/* Floating Header controls */}
      <div className="absolute top-8 inset-x-6 max-w-md mx-auto flex items-center justify-between z-10">
        <button onClick={() => handleNavigate('poems')} className={`p-2 opacity-50 hover:opacity-100 transition-opacity ${theme?.text}`}>
          <ArrowLeft size={20} />
        </button>

        {/* Position Indicator */}
        {allPoems.length > 0 && (
          <div className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme?.text}`}>
            {currentIndex + 1} / {allPoems.length}
          </div>
        )}
        
        <ThemeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} theme={theme} />
      </div>

      {/* Minimalist Sliding Navigation Arrows - Desktop Only */}
      {prevPoemId && (
        <button onClick={() => { setSwipeDirection('right'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, prevPoemId); }, 400); }} className={`hidden md:block fixed left-0 md:left-4 lg:left-12 top-1/2 -translate-y-1/2 p-3 opacity-40 hover:opacity-100 transition-opacity duration-300 z-50 ${theme?.text}`} title="Newer Poem">
          <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 animate-slide-left" strokeWidth={1.5} />
        </button>
      )}
      {nextPoemId && (
        <button onClick={() => { setSwipeDirection('left'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, nextPoemId); }, 400); }} className={`hidden md:block fixed right-0 md:right-4 lg:right-12 top-1/2 -translate-y-1/2 p-3 opacity-40 hover:opacity-100 transition-opacity duration-300 z-50 ${theme?.text}`} title="Older Poem">
          <ChevronRight className="w-6 h-6 md:w-8 md:h-8 animate-slide-right" strokeWidth={1.5} />
        </button>
      )}

      {/* Main Poem Focus Area */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full py-24 space-y-4 md:space-y-10">
        
        {/* Mobile Up Navigation Arrow */}
        {prevPoemId && (
          <button onClick={() => { setSwipeDirection('down'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, prevPoemId); }, 400); }} className={`md:hidden p-2 opacity-40 hover:opacity-100 transition-opacity z-40 ${theme?.text}`} title="Newer Poem">
            <ChevronUp className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}

        {/* Pinterest-style Card */}
        <div 
          ref={cardRef} 
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={`relative ${isDownloading ? 'w-[360px] h-[640px] rounded-none' : 'w-full rounded-[40px]'} overflow-hidden shadow-2xl group transition-all touch-none antialiased [text-rendering:geometricPrecision] will-change-transform ${
            swipeDirection === 'left' 
              ? '-translate-x-12 opacity-0 blur-[4px] duration-500 ease-premium' 
              : swipeDirection === 'right' 
                ? 'translate-x-12 opacity-0 blur-[4px] duration-500 ease-premium' 
                : swipeDirection === 'up'
                  ? '-translate-y-12 opacity-0 blur-[4px] duration-500 ease-premium'
                  : swipeDirection === 'down'
                    ? 'translate-y-12 opacity-0 blur-[4px] duration-500 ease-premium'
                    : 'animate-cinematic-rise'
          } ${isDarkMode ? 'bg-stone-900' : 'bg-stone-50'}`}
        >
          {/* Background Layer */}
          <div 
            className="absolute inset-0 z-0 bg-cover bg-bottom transition-transform duration-1000 group-hover:scale-105"
            style={{ backgroundImage: "url('/Whatsapp%20Background%20Quote.png')" }}
          />
          
          {/* Night Mode Overlay */}
          <div className={`absolute inset-0 z-10 transition-colors duration-700 pointer-events-none ${isDarkMode ? 'bg-black/60' : 'bg-transparent'}`} />
          
          {/* Card Content */}
          <div className={`relative z-50 flex flex-col items-center justify-center ${isDownloading ? 'h-[640px] px-8 pt-6 pb-48' : 'min-h-[75vh] md:min-h-[85vh] px-8 pt-6 pb-48 md:px-10 md:pt-8 md:pb-64'} ${theme?.text}`}>
            
            {/* Text Wrapper */}
            <div className="flex flex-col items-start w-full max-w-sm sm:max-w-md space-y-8 md:space-y-6 z-10">
              
              {/* Poem Title */}
              {poem.title && (
                <h2 className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-center drop-shadow-md opacity-80 mb-2 w-full">
                  {poem.title}
                </h2>
              )}

              <p className={`font-special-elite text-left drop-shadow-lg whitespace-pre-wrap w-full leading-relaxed ${getPoemFontSizeClass(poem?.text?.length, isDownloading)}`}>
                {poem.text}
              </p>
              
              <div className="flex flex-col items-start space-y-2 pt-4 w-full">
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] opacity-90 drop-shadow-md">
                  {poem.author || 'Ben Hasan'}
                </p>
                {(poem.date || poem.year) && (
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-60 drop-shadow-md">
                    {poem.date 
                      ? (safeParseDate(poem.date) ? safeParseDate(poem.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : poem.date) 
                      : poem.year}
                  </p>
                )}
                {poem.tags && (
                  <div className="flex flex-wrap gap-2 pt-4 w-full">
                    {(Array.isArray(poem.tags) ? poem.tags : poem.tags.split(',')).map((tag, i) => (
                      <span key={i} className={`text-[8px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border opacity-80 drop-shadow-md ${isDarkMode ? 'border-stone-100/20 bg-stone-100/10' : 'border-stone-900/10 bg-stone-900/5'}`}>
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Mobile Down Navigation Arrow */}
        {nextPoemId && (
          <button onClick={() => { setSwipeDirection('up'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-poem', null, null, nextPoemId); }, 400); }} className={`md:hidden p-2 opacity-40 hover:opacity-100 transition-opacity z-40 ${theme?.text}`} title="Older Poem">
            <ChevronDown className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}

      </div>

      {/* Floating Action Dock */}
      <div className={`fixed bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center gap-6 md:gap-8 px-6 py-3 md:px-8 md:py-4 rounded-full shadow-2xl backdrop-blur-md border transition-all duration-500 ${
        isDarkMode ? 'bg-black/50 border-white/10' : 'bg-white/80 border-stone-200/50'
      }`}>
        {/* Like */}
        <button onClick={handleLike} className={`group flex items-center gap-2 transition-all duration-300 ${isLiked ? 'text-red-500' : (isDarkMode ? 'text-stone-300 hover:text-red-400' : 'text-stone-600 hover:text-red-500')}`}>
          <Heart size={20} strokeWidth={1.5} fill={isLiked ? 'currentColor' : 'transparent'} className={`${isLiked ? 'animate-in zoom-in duration-300' : 'group-hover:scale-110'} transition-all`} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{poem.likes || 0}</span>
        </button>

        <div className={`w-px h-4 ${isDarkMode ? 'bg-white/20' : 'bg-stone-300'}`}></div>

        {/* Share */}
        <button onClick={handleShare} className={`group flex items-center gap-2 transition-all duration-300 ${isDarkMode ? 'text-stone-300 hover:text-emerald-400' : 'text-stone-600 hover:text-emerald-500'}`}>
          <Share size={20} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
          <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Share</span>
        </button>

        <div className={`w-px h-4 ${isDarkMode ? 'bg-white/20' : 'bg-stone-300'}`}></div>

        {/* Save */}
        <button onClick={handleDownload} disabled={isDownloading} className={`group flex items-center gap-2 transition-all duration-300 ${isDarkMode ? 'text-stone-300 hover:text-blue-400' : 'text-stone-600 hover:text-blue-500'}`}>
          {isDownloading ? <Loader2 size={20} strokeWidth={1.5} className="animate-spin" /> : <Download size={20} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />}
          <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Save</span>
        </button>
      </div>

      {/* The Ecosystem Trap */}
      <div className="pb-32 md:pb-24 flex justify-center">
        <button 
          onClick={() => handleNavigate('poems')}
          style={pillStyle}
          className="px-8 py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-80 transition-all shadow-xl"
        >
          Explore Poetry Archive
        </button>
      </div>
      
      {toast && <Toast message={toast.message} type={toast.type} isDarkMode={isDarkMode} onClose={() => setToast(null)} />}
    </div>
  );
};

export default SinglePoem;
